import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import api, { API_BASE } from '../api';
import { useAuth } from '../context/auth';
import { PageTitle, LoadingText } from '../components/Style';
import Logout from '../components/Logout';
import { DisplayPost } from '../components/DisplayPost';
import CTACard from '../components/CTACard';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import UserBadge, { isSpecialAvatarUser, SpecialAvatar } from '../components/UserBadge';
import ShareModal from '../components/ShareModal';
import SignupCTA from '../components/SignupCTA';
import { trackEvent } from '../analytics';

var ProfileCard = styled.div`
  box-sizing: border-box;
  position: relative;
  max-width: 400px;
  margin: 2rem auto 1.5rem;
  padding: 1.5rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: white;
  border: 1px solid #ddd;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 12px;
`;

var ShareButton = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #888;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #000;
  }
`;

var ProfilePic = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 8px;
  object-fit: cover;
  margin-bottom: 1rem;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
`;

var ProfilePicPlaceholder = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 8px;
  background: #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: #999;
  font-size: 0.9rem;
`;

var Bio = styled.p`
  color: #333;
  text-align: center;
  margin: 0.5rem 0 1rem;
  line-height: 1.5;
`;

var LinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

var ProfileLink = styled.a`
  color: #0066cc;
  font-size: 0.85rem;
  text-decoration: none;
  overflow-wrap: break-word;
  word-break: break-word;
  &:hover {
    text-decoration: underline;
  }
`;

var EditLink = styled(Link)`
  background: white;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  color: black;
  font-weight: 700;
  font-size: 0.8rem;
  text-decoration: none;
  text-align: center;
`;

var ProfileActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
  width: 100%;

  a,
  button {
    flex: 1 1 0;
    min-width: 0;
    margin-bottom: 0;
    white-space: nowrap;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

var FollowButton = styled.button`
  background: ${function (props) {
    return props.isFollowing ? 'black' : 'white';
  }};
  color: ${function (props) {
    return props.isFollowing ? 'white' : 'black';
  }};
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  margin-top: 0.75rem;
`;

var FollowCounts = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 0.5rem;
`;

var FollowCountItem = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;

  &:hover span {
    color: #000;
  }
`;

var FollowCountNumber = styled.span`
  font-size: 1.1rem;
  font-weight: 700;
  color: #000;
`;

var FollowCountLabel = styled.span`
  font-size: 0.7rem;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 0.1rem;
`;

var LocationText = styled.div`
  font-size: 0.85rem;
  color: #888;
  margin-top: 0.25rem;
`;

var UsernameSecondary = styled.div`
  font-size: 0.9rem;
  color: #888;
  margin-top: -0.25rem;
`;

var BadgesSection = styled.div`
  display: flex;
  gap: 0.5em;
  margin-top: 0.75rem;
  font-size: 0.85rem;
`;

var ProfileTabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin: 0.75rem 0;
`;

var ProfileTab = styled.button`
  background: none;
  border: none;
  border-bottom: 2px solid ${function (props) {
      return props.$active ? '#000' : 'transparent';
    }};
  padding: 0.3em 0;
  font-size: 0.85rem;
  font-family: inherit;
  font-weight: ${function (props) {
    return props.$active ? '700' : '400';
  }};
  color: ${function (props) {
    return props.$active ? '#000' : '#999';
  }};
  cursor: pointer;

  &:hover {
    color: #000;
  }
`;

var PostsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
  align-items: center;
  max-width: 410px;
  margin: 0 auto;
  padding: 0 0.5em;
`;

var FollowListOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
`;

var FollowListCard = styled.div`
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 360px;
  max-height: 70vh;
  overflow-y: auto;
`;

var FollowListTitle = styled.h3`
  margin: 0;
  padding: 16px;
  font-size: 1rem;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  background: white;
  border-radius: 16px 16px 0 0;
`;

var FollowListItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  text-decoration: none;
  color: inherit;

  &:hover {
    background: #f5f5f5;
  }
`;

var FollowListPic = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
`;

var FollowListAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 0.85rem;
  flex-shrink: 0;
`;

var FollowListName = styled.span`
  font-weight: 600;
  font-size: 0.9rem;
`;

var FollowListEmpty = styled.p`
  text-align: center;
  color: #999;
  font-size: 0.85rem;
  padding: 24px 16px;
`;

var FollowListButton = styled.button`
  margin-left: auto;
  background: ${function (props) {
    return props.isFollowing ? 'black' : 'white';
  }};
  color: ${function (props) {
    return props.isFollowing ? 'white' : 'black';
  }};
  border: 1px solid black;
  border-radius: 6px;
  padding: 0.3rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
`;

function Profile() {
  var { userId } = useParams();

  var authContext = useAuth();
  var authTokens = authContext.authTokens;

  var profileState = useState(null);
  var profile = profileState[0];
  var setProfile = profileState[1];

  var errorState = useState('');
  var error = errorState[0];
  var setError = errorState[1];

  var resolvedIdState = useState(null);
  var resolvedId = resolvedIdState[0];
  var setResolvedId = resolvedIdState[1];

  var profileTabState = useState('all');
  var profileTab = profileTabState[0];
  var setProfileTab = profileTabState[1];

  // isOwnProfile is derived from the loaded profile further down, which is too
  // late for this callback, so compare against the resolved id instead.
  var viewingOwnProfile = !!authTokens?.user?.id && authTokens.user.id === resolvedId;

  var fetchPostsPage = useCallback(
    function (cursor) {
      if (!resolvedId) return Promise.resolve({ posts: [], hasMore: false, nextCursor: null });

      // Your own Lists tab goes through my-lists so drafts show up. Every other
      // path only ever returns published posts.
      if (profileTab === 'lists' && viewingOwnProfile) {
        return api.get('/api/lists/my-lists').then(function (response) {
          return { posts: response.data || [], hasMore: false, nextCursor: null };
        });
      }

      var url = '/api/posts/user/' + resolvedId;
      var params = [];
      if (cursor) params.push('before=' + encodeURIComponent(cursor));
      params.push('limit=20');
      if (profileTab === 'lists') params.push('kind=list');
      // Posts is everything that is not a list, asks included.
      else if (profileTab === 'posts') params.push('kind=single,ask');
      if (params.length) url += '?' + params.join('&');
      return api.get(url).then(function (response) {
        return response.data;
      });
    },
    [resolvedId, profileTab, viewingOwnProfile]
  );

  var scroll = useInfiniteScroll(fetchPostsPage, [resolvedId, profileTab]);
  var posts = scroll.posts;
  var postsLoading = scroll.loading;
  var loadingMore = scroll.loadingMore;
  var hasMore = scroll.hasMore;
  var sentinelRef = scroll.sentinelRef;
  var removePost = scroll.removePost;

  var bookmarkState = useState({});
  var bookmarkedMap = bookmarkState[0];
  var setBookmarkedMap = bookmarkState[1];

  var libraryAddState = useState({});
  var libraryAddMap = libraryAddState[0];
  var setLibraryAddMap = libraryAddState[1];

  var reactionsState = useState({});
  var userReactionsMap = reactionsState[0];
  var setUserReactionsMap = reactionsState[1];

  var commentLikeState = useState({});
  var likedCommentsMap = commentLikeState[0];
  var setLikedCommentsMap = commentLikeState[1];

  var repostState = useState({});
  var repostedMap = repostState[0];
  var setRepostedMap = repostState[1];

  var followCountsState = useState(null);
  var followCounts = followCountsState[0];
  var setFollowCounts = followCountsState[1];

  var followedIdsState = useState({});
  var followedIds = followedIdsState[0];
  var setFollowedIds = followedIdsState[1];

  var isFollowingState = useState(false);
  var isFollowing = isFollowingState[0];
  var setIsFollowing = isFollowingState[1];

  var followListState = useState(null); // null | "followers" | "following"
  var followListType = followListState[0];
  var setFollowListType = followListState[1];

  var followListDataState = useState([]);
  var followListData = followListDataState[0];
  var setFollowListData = followListDataState[1];

  var followListLoadingState = useState(false);
  var followListLoading = followListLoadingState[0];
  var setFollowListLoading = followListLoadingState[1];

  var dismissedCardsState = useState([]);
  var dismissedCards = dismissedCardsState[0];
  var setDismissedCards = dismissedCardsState[1];

  var shareState = useState(false);
  var showShareModal = shareState[0];
  var setShowShareModal = shareState[1];

  var isLoggedIn = authTokens?.token;

  useEffect(
    function () {
      if (profile) {
        document.title = '@' + profile.username + ' | HeavyRotation';
      }
    },
    [profile]
  );

  useEffect(
    function () {
      if (!userId) return;
      setResolvedId(null);
      api
        .get('/api/users/profile/' + userId)
        .then(function (result) {
          setProfile(result.data);
          setResolvedId(result.data.id);
          if (result.data.dismissedCards && authTokens?.user?.id === result.data.id) {
            setDismissedCards(
              result.data.dismissedCards.split(',').map(function (s) {
                return s.trim();
              })
            );
          }
          // Store referrer for unauthenticated visitors (only from explicit ?ref= links)
          if (!isLoggedIn) {
            var refParam = new URLSearchParams(window.location.search).get('ref');
            if (refParam) {
              localStorage.setItem('signup_referrer', refParam);
            }
          }
        })
        .catch(function () {
          setError('Failed to load profile.');
        });
    },
    [userId, authTokens?.user?.id, isLoggedIn]
  );

  useEffect(
    function () {
      if (!resolvedId) return;
      api
        .get('/api/follows/counts/' + resolvedId)
        .then(function (result) {
          setFollowCounts(result.data);
        })
        .catch(function () {});
      if (isLoggedIn) {
        api
          .get('/api/follows')
          .then(function (result) {
            var ids = result.data || [];
            setIsFollowing(ids.indexOf(resolvedId) !== -1);
            var map = {};
            for (var i = 0; i < ids.length; i++) {
              map[ids[i]] = true;
            }
            setFollowedIds(map);
          })
          .catch(function () {});
        api
          .get('/api/bookmarks')
          .then(function (result) {
            var map = {};
            var ids = result.data || [];
            for (var i = 0; i < ids.length; i++) {
              map[ids[i]] = true;
            }
            setBookmarkedMap(map);
          })
          .catch(function () {});
        api
          .get('/api/library-adds')
          .then(function (result) {
            var map = {};
            var ids = result.data || [];
            for (var i = 0; i < ids.length; i++) {
              map[ids[i]] = true;
            }
            setLibraryAddMap(map);
          })
          .catch(function () {});
        api
          .get('/api/post-reactions')
          .then(function (result) {
            var map = {};
            var items = result.data || [];
            for (var i = 0; i < items.length; i++) {
              var pid = items[i].postId;
              if (!map[pid]) map[pid] = [];
              map[pid].push(items[i].emoji);
            }
            setUserReactionsMap(map);
          })
          .catch(function () {});
        api
          .get('/api/comment-likes')
          .then(function (result) {
            var map = {};
            var ids = result.data || [];
            for (var i = 0; i < ids.length; i++) {
              map[ids[i]] = true;
            }
            setLikedCommentsMap(map);
          })
          .catch(function () {});
        api
          .get('/api/reposts')
          .then(function (result) {
            var map = {};
            var ids = result.data || [];
            for (var i = 0; i < ids.length; i++) {
              map[ids[i]] = true;
            }
            setRepostedMap(map);
          })
          .catch(function () {});
      }
    },
    [resolvedId, isLoggedIn]
  );

  function openFollowList(type) {
    setFollowListType(type);
    setFollowListLoading(true);
    setFollowListData([]);
    api
      .get('/api/follows/' + type + '/' + resolvedId)
      .then(function (result) {
        setFollowListData(result.data || []);
        setFollowListLoading(false);
      })
      .catch(function () {
        setFollowListLoading(false);
      });
  }

  function handleFollowToggle() {
    var newFollowing = !isFollowing;
    if (newFollowing && profile) {
      var previousPath = null;
      try {
        previousPath = sessionStorage.getItem('hr_previous_path');
      } catch (e) {}
      trackEvent('follow_completed', {
        source: 'profile',
        target_user: profile.username,
        previous_page: previousPath,
      });
    }
    setIsFollowing(newFollowing);
    setFollowedIds(function (prev) {
      var next = {};
      for (var k in prev) {
        if (prev.hasOwnProperty(k)) next[k] = prev[k];
      }
      if (newFollowing) {
        next[resolvedId] = true;
      } else {
        delete next[resolvedId];
      }
      return next;
    });
    if (followCounts) {
      setFollowCounts({
        followerCount: followCounts.followerCount + (newFollowing ? 1 : -1),
        followingCount: followCounts.followingCount,
      });
    }
    api.post('/api/follows/' + resolvedId).catch(function () {
      setIsFollowing(!newFollowing);
      setFollowedIds(function (prev) {
        var next = {};
        for (var k in prev) {
          if (prev.hasOwnProperty(k)) next[k] = prev[k];
        }
        if (!newFollowing) {
          next[userId] = true;
        } else {
          delete next[userId];
        }
        return next;
      });
      if (followCounts) {
        setFollowCounts({
          followerCount: followCounts.followerCount,
          followingCount: followCounts.followingCount,
        });
      }
    });
  }

  function handleListFollowToggle(targetId) {
    var wasFollowing = !!followedIds[targetId];
    setFollowedIds(function (prev) {
      var next = {};
      for (var k in prev) {
        if (prev.hasOwnProperty(k)) next[k] = prev[k];
      }
      if (wasFollowing) {
        delete next[targetId];
      } else {
        next[targetId] = true;
      }
      return next;
    });
    if (targetId === resolvedId) {
      setIsFollowing(!wasFollowing);
      if (followCounts) {
        setFollowCounts({
          followerCount: followCounts.followerCount + (wasFollowing ? -1 : 1),
          followingCount: followCounts.followingCount,
        });
      }
    }
    api.post('/api/follows/' + targetId).catch(function () {
      setFollowedIds(function (prev) {
        var next = {};
        for (var k in prev) {
          if (prev.hasOwnProperty(k)) next[k] = prev[k];
        }
        if (wasFollowing) {
          next[targetId] = true;
        } else {
          delete next[targetId];
        }
        return next;
      });
      if (targetId === resolvedId) {
        setIsFollowing(wasFollowing);
        if (followCounts) {
          setFollowCounts({
            followerCount: followCounts.followerCount,
            followingCount: followCounts.followingCount,
          });
        }
      }
    });
  }

  function handleBookmarkToggle(postId, bookmarked) {
    setBookmarkedMap(function (prev) {
      var next = {};
      for (var key in prev) {
        if (prev.hasOwnProperty(key)) {
          next[key] = prev[key];
        }
      }
      next[postId] = bookmarked;
      return next;
    });
  }

  function handleReactionToggle(postId, emoji, reacted) {
    setUserReactionsMap(function (prev) {
      var next = {};
      for (var key in prev) {
        if (prev.hasOwnProperty(key)) {
          next[key] = (prev[key] || []).slice();
        }
      }
      if (!next[postId]) next[postId] = [];
      if (reacted) {
        if (next[postId].indexOf(emoji) === -1) next[postId].push(emoji);
      } else {
        next[postId] = next[postId].filter(function (e) {
          return e !== emoji;
        });
      }
      return next;
    });
  }

  function handleCommentLikeToggle(commentId, liked) {
    setLikedCommentsMap(function (prev) {
      var next = {};
      for (var key in prev) {
        if (prev.hasOwnProperty(key)) {
          next[key] = prev[key];
        }
      }
      next[commentId] = liked;
      return next;
    });
  }

  function handleRepostToggle(postId, reposted) {
    setRepostedMap(function (prev) {
      var next = {};
      for (var key in prev) {
        if (prev.hasOwnProperty(key)) {
          next[key] = prev[key];
        }
      }
      next[postId] = reposted;
      return next;
    });
  }

  function handlePostDelete(postId) {
    removePost(postId);
  }

  function handleShareProfile() {
    setShowShareModal(true);
  }

  if (error) {
    return (
      <ProfileCard>
        <PageTitle>Profile</PageTitle>
        <p style={{ color: 'tomato' }}>{error}</p>
      </ProfileCard>
    );
  }

  if (!profile) {
    return <LoadingText>Loading...</LoadingText>;
  }

  var isOwnProfile = authTokens?.user?.id === profile.id;

  var pictureURL = profile.profilePictureURL ? API_BASE + profile.profilePictureURL : '';

  var linksList = [];
  if (profile.links) {
    linksList = profile.links
      .split(',')
      .map(function (l) {
        return l.trim();
      })
      .filter(function (l) {
        return l.length > 0;
      });
  }

  return (
    <>
      <ProfileCard>
        <ShareButton onClick={handleShareProfile} title="Share profile">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
            <polyline points="16 6 12 2 8 6" />
            <line x1="12" y1="2" x2="12" y2="15" />
          </svg>
        </ShareButton>
        {isSpecialAvatarUser(profile.username) ? (
          <div style={{ marginBottom: '1rem' }}>
            <SpecialAvatar size={120} />
          </div>
        ) : pictureURL ? (
          <ProfilePic src={pictureURL} alt={profile.username} />
        ) : (
          <ProfilePicPlaceholder>No photo</ProfilePicPlaceholder>
        )}

        {profile.displayName ? (
          <>
            <PageTitle style={{ fontWeight: 900 }}>{profile.displayName}</PageTitle>
            <UsernameSecondary>@{profile.username}</UsernameSecondary>
          </>
        ) : (
          <PageTitle style={{ fontWeight: 900 }}>@{profile.username}</PageTitle>
        )}

        {profile.location && <LocationText>{profile.location}</LocationText>}

        {profile.badges && profile.badges.length > 0 && (
          <BadgesSection>
            <UserBadge badges={profile.badges} size="medium" showLabel />
          </BadgesSection>
        )}

        {followCounts && (
          <FollowCounts>
            <FollowCountItem
              onClick={function () {
                openFollowList('followers');
              }}
            >
              <FollowCountNumber>{followCounts.followerCount}</FollowCountNumber>
              <FollowCountLabel>
                {followCounts.followerCount === 1 ? 'Follower' : 'Followers'}
              </FollowCountLabel>
            </FollowCountItem>
            <FollowCountItem
              onClick={function () {
                openFollowList('following');
              }}
            >
              <FollowCountNumber>{followCounts.followingCount}</FollowCountNumber>
              <FollowCountLabel>Following</FollowCountLabel>
            </FollowCountItem>
          </FollowCounts>
        )}

        {isLoggedIn && !isOwnProfile && (
          <FollowButton isFollowing={isFollowing} onClick={handleFollowToggle}>
            {isFollowing ? 'Following' : 'Follow'}
          </FollowButton>
        )}

        {(profile.bio || linksList.length > 0) && (
          <hr
            style={{
              width: '100%',
              border: 'none',
              borderTop: '1px solid #eee',
              margin: '0.75rem 0 0.25rem',
            }}
          />
        )}

        {profile.bio && <Bio>{profile.bio}</Bio>}

        {linksList.length > 0 && (
          <LinksContainer>
            {linksList.map(function (link, i) {
              var href = link;
              if (href.indexOf('http://') !== 0 && href.indexOf('https://') !== 0) {
                href = 'https://' + href;
              }
              return (
                <ProfileLink key={i} href={href} target="_blank" rel="noopener noreferrer">
                  {link}
                </ProfileLink>
              );
            })}
          </LinksContainer>
        )}

        {isOwnProfile && (
          <ProfileActions>
            <EditLink to="/profile/edit">Edit Profile</EditLink>
            <EditLink to="/streaming">Streaming Preferences</EditLink>
            <EditLink to="/bookmarks">Bookmarks</EditLink>
            <Logout />
          </ProfileActions>
        )}
      </ProfileCard>

      {!isLoggedIn && <SignupCTA style={{ marginBottom: '1.5rem' }} />}

      {isOwnProfile && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            marginBottom: '1.5rem',
          }}
        >
          <CTACard
            title="Music is better with friends"
            subtitle="Invite friends to join Heavy Rotation and share music together."
            to="/invites"
            buttonText="Invite Friends"
            shareUrl={'https://heavyrotation.app/profile/' + (authTokens?.user?.username || '')}
            shareImageData={{
              profilePicURL: profile.profilePictureURL,
              displayName: profile.displayName,
              username: profile.username,
              recentArtwork: posts
                .slice(0, 9)
                .map(function (p) {
                  return p.media?.artwork_url;
                })
                .filter(Boolean),
            }}
            dismissKey="profile_invite"
            dismissed={dismissedCards.indexOf('profile_invite') !== -1}
            onDismiss={function (key) {
              setDismissedCards(function (prev) {
                if (prev.indexOf(key) !== -1) return prev;
                return prev.concat([key]);
              });
              api.post('/api/users/dismiss-card', { key: key }).catch(function () {});
            }}
          />
        </div>
      )}

      <ProfileTabs>
        {['all', 'posts', 'lists'].map(function (tab) {
          var isActive = profileTab === tab;
          return (
            <ProfileTab
              key={tab}
              $active={isActive}
              onClick={function () {
                setProfileTab(tab);
              }}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </ProfileTab>
          );
        })}
      </ProfileTabs>

      {postsLoading ? (
        <LoadingText>Loading posts...</LoadingText>
      ) : posts.length > 0 ? (
        <PostsContainer>
          {posts.map(function (post) {
            return (
              <DisplayPost
                post={post}
                key={post.repostId || post.id}
                isBookmarked={!!bookmarkedMap[post.id]}
                userReactions={userReactionsMap[post.id] || []}
                isAddedToLibrary={!!libraryAddMap[post.id]}
                isFollowing={isFollowing}
                isReposted={!!repostedMap[post.id]}
                onBookmarkToggle={handleBookmarkToggle}
                onReactionToggle={handleReactionToggle}
                followSource="profile"
                onFollowToggle={function () {
                  handleFollowToggle();
                }}
                likedCommentsMap={likedCommentsMap}
                onCommentLikeToggle={handleCommentLikeToggle}
                onPostDelete={handlePostDelete}
                onRepostToggle={handleRepostToggle}
              />
            );
          })}
          <div ref={sentinelRef} />
          {loadingMore && <LoadingText>Loading more...</LoadingText>}
        </PostsContainer>
      ) : null}
      {showShareModal && (
        <ShareModal
          url={'https://heavyrotation.app/profile/' + userId}
          title="Share Profile"
          text="Follow me on HeavyRotation.app to discover and share music that you love."
          shareType="profile"
          imageData={{
            profilePicURL: profile.profilePictureURL,
            displayName: profile.displayName,
            username: profile.username,
            recentArtwork: posts
              .slice(0, 9)
              .map(function (p) {
                return p.media?.artwork_url;
              })
              .filter(Boolean),
          }}
          onClose={function () {
            setShowShareModal(false);
          }}
        />
      )}
      {followListType && (
        <FollowListOverlay
          onClick={function () {
            setFollowListType(null);
          }}
        >
          <FollowListCard
            onClick={function (e) {
              e.stopPropagation();
            }}
          >
            <FollowListTitle>
              {followListType === 'followers' ? 'Followers' : 'Following'}
            </FollowListTitle>
            {followListLoading && <FollowListEmpty>Loading...</FollowListEmpty>}
            {!followListLoading && followListData.length === 0 && (
              <FollowListEmpty>
                {followListType === 'followers' ? 'No followers yet' : 'Not following anyone yet'}
              </FollowListEmpty>
            )}
            {!followListLoading &&
              followListData.map(function (u) {
                var isSelf = authTokens?.user?.id === u.id;
                var isFollowingUser = !!followedIds[u.id];
                return (
                  <div
                    key={u.id}
                    style={{ display: 'flex', alignItems: 'center', padding: '0 16px 0 0' }}
                  >
                    <FollowListItem
                      to={'/profile/' + u.username}
                      onClick={function () {
                        setFollowListType(null);
                      }}
                      style={{ flex: 1, minWidth: 0 }}
                    >
                      {isSpecialAvatarUser(u.username) ? (
                        <SpecialAvatar size={36} />
                      ) : u.profilePictureURL ? (
                        <FollowListPic src={API_BASE + u.profilePictureURL} alt={u.username} />
                      ) : (
                        <FollowListAvatar>
                          {u.username ? u.username.charAt(0).toUpperCase() : '?'}
                        </FollowListAvatar>
                      )}
                      <div style={{ minWidth: 0 }}>
                        <FollowListName>{u.displayName || u.username}</FollowListName>
                        {u.displayName && (
                          <div style={{ color: '#777', fontSize: '0.75rem' }}>@{u.username}</div>
                        )}
                      </div>
                    </FollowListItem>
                    {isLoggedIn && !isSelf && (
                      <FollowListButton
                        isFollowing={isFollowingUser}
                        onClick={function () {
                          handleListFollowToggle(u.id);
                        }}
                      >
                        {isFollowingUser ? 'Following' : 'Follow'}
                      </FollowListButton>
                    )}
                  </div>
                );
              })}
          </FollowListCard>
        </FollowListOverlay>
      )}
    </>
  );
}

export default Profile;
